import { useCallback, useEffect, useRef, useState } from 'react';
import { dashboardService } from '../../../api/services/dashboardService';
import {
  ApiError,
  AppIdMetricsDetail,
  AppIdMetricsOverview,
  AppMetricsResponse,
  RetentionData,
  TableCountsResponse,
} from '../../../api/types';
import { getUserFriendlyErrorMessage } from '../../../api/utils/errorHandler';
import { getCountryCode } from '../constants/countries';
import { normalizeRetentionCountries } from '../utils/retentionCountries';
import {
  DASHBOARD_RETENTION_COUNTRIES_STORAGE_KEY,
  readPersistedRetentionCountries,
} from '../utils/dashboardRetentionCountriesStorage';
import {
  DEFAULT_DASHBOARD_SECTION_VISIBILITY,
  type DashboardSectionVisibility,
} from './useDashboardSectionVisibility';

type MetricsDetailType =
  | 'app_session'
  | 'users'
  | 'game_session'
  | 'test_session'
  | 'chat_session'
  | 'advertisements';

const DETAIL_TYPES: MetricsDetailType[] = [
  'app_session',
  'users',
  'game_session',
  'test_session',
  'chat_session',
  'advertisements',
];

function detailStepLabel(type: MetricsDetailType): string {
  const labels: Record<MetricsDetailType, string> = {
    app_session: 'Uygulama oturumu grafikleri',
    users: 'Kullanıcı grafikleri',
    game_session: 'Oyun oturumu grafikleri',
    test_session: 'Test oturumu grafikleri',
    chat_session: 'Sohbet oturumu grafikleri',
    advertisements: 'Reklam grafikleri',
  };
  return labels[type];
}

export function useDashboardData(
  sectionVisibility: DashboardSectionVisibility = DEFAULT_DASHBOARD_SECTION_VISIBILITY
) {
  const [retentionCountries, setRetentionCountriesState] = useState<string[]>(readPersistedRetentionCountries);
  const retentionCountriesRef = useRef(retentionCountries);
  retentionCountriesRef.current = retentionCountries;

  useEffect(() => {
    try {
      localStorage.setItem(DASHBOARD_RETENTION_COUNTRIES_STORAGE_KEY, JSON.stringify(retentionCountries));
    } catch (err) {
      console.warn('Dashboard ülke seçimi kaydedilemedi:', err);
    }
  }, [retentionCountries]);

  const setRetentionCountries = useCallback((next: string[] | ((prev: string[]) => string[])) => {
    setRetentionCountriesState((prev) => {
      const resolved = typeof next === 'function' ? (next as (p: string[]) => string[])(prev) : next;
      return normalizeRetentionCountries(resolved);
    });
  }, []);

  const selectedCountry = retentionCountries[0] ?? 'Tüm Ülkeler';

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [tableCounts, setTableCounts] = useState<TableCountsResponse | null>(null);
  const [retentionData, setRetentionData] = useState<RetentionData[]>([]);
  const [appMetricsData, setAppMetricsData] = useState<AppMetricsResponse[]>([]);
  const [overviewMetrics, setOverviewMetrics] = useState<AppIdMetricsOverview[]>([]);
  const [appSessionMetrics, setAppSessionMetrics] = useState<AppIdMetricsDetail[]>([]);
  const [userMetrics, setUserMetrics] = useState<AppIdMetricsDetail[]>([]);
  const [gameSessionMetrics, setGameSessionMetrics] = useState<AppIdMetricsDetail[]>([]);
  const [testSessionMetrics, setTestSessionMetrics] = useState<AppIdMetricsDetail[]>([]);
  const [chatSessionMetrics, setChatSessionMetrics] = useState<AppIdMetricsDetail[]>([]);
  const [advertisementMetrics, setAdvertisementMetrics] = useState<AppIdMetricsDetail[]>([]);
  const [loading, setLoading] = useState(true);
  /** Retention tablosu ülke / yenile sırasında. */
  const [retentionScopedLoading, setRetentionScopedLoading] = useState(false);
  /** Uygulama metrikleri ülke / yenile sırasında. */
  const [appMetricsScopedLoading, setAppMetricsScopedLoading] = useState(false);
  /** Tam ekran overlay (retention veya app metrik veya ikisi). */
  const countryScopedLoading = retentionScopedLoading || appMetricsScopedLoading;
  /** Header / tablo Yenile: tüm loadFullDashboard bitene kadar tam ekran popup. */
  const [dashboardBlockingLoading, setDashboardBlockingLoading] = useState(false);
  /** Sadece tam dashboard yenilemede: tamamlanan adım sayısı + açıklama (ilerleme paneli). */
  const [dashboardRefreshProgress, setDashboardRefreshProgress] = useState<{
    completed: number;
    total: number;
    label: string;
  } | null>(null);
  const retentionRequestId = useRef(0);
  const appMetricsRequestId = useRef(0);
  /** Versiyon tabloları için: overview API dönene kadar false (tableCounts sonrası loading false olsa bile). */
  const [overviewReady, setOverviewReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const shouldLoadTableCounts = sectionVisibility.statCards;
  const shouldLoadRetention = sectionVisibility.retentionTable;
  const shouldLoadAppMetrics = sectionVisibility.appMetricsTable;
  const shouldLoadCharts = sectionVisibility.primaryCharts || sectionVisibility.chatAdsCharts;

  const setDetailMetrics = useCallback((type: MetricsDetailType, data: AppIdMetricsDetail[]) => {
    switch (type) {
      case 'app_session':
        setAppSessionMetrics(data);
        break;
      case 'users':
        setUserMetrics(data);
        break;
      case 'game_session':
        setGameSessionMetrics(data);
        break;
      case 'test_session':
        setTestSessionMetrics(data);
        break;
      case 'chat_session':
        setChatSessionMetrics(data);
        break;
      case 'advertisements':
        setAdvertisementMetrics(data);
        break;
      default:
        break;
    }
  }, []);

  /** Tüm dashboard verileri: her API çağrısı bir önceki tamamlanmadan başlamaz. */
  const loadFullDashboard = useCallback(async (opts?: { blockingOverlay?: boolean }) => {
    const blockingOverlay = opts?.blockingOverlay ?? false;
    const countriesSnapshot = retentionCountriesRef.current;
    setLoading(true);
    setOverviewReady(false);
    setError(null);
    const fullRefreshSteps =
      (shouldLoadTableCounts ? 1 : 0) +
      (shouldLoadRetention ? 1 : 0) +
      (shouldLoadAppMetrics ? 1 : 0) +
      (shouldLoadCharts ? 1 + DETAIL_TYPES.length : 0);

    let completedSteps = 0;
    const reportStep = (label: string) => {
      if (blockingOverlay) {
        setDashboardRefreshProgress({
          completed: completedSteps,
          total: fullRefreshSteps || 1,
          label,
        });
      }
    };
    const finishStep = () => {
      completedSteps += 1;
    };

    if (blockingOverlay) {
      setDashboardBlockingLoading(true);
      reportStep(fullRefreshSteps > 0 ? 'Veriler hazırlanıyor…' : 'Güncellenecek açık bölüm yok');
    }

    try {
      if (shouldLoadTableCounts) {
        reportStep('Özet tablo sayıları yükleniyor…');
        try {
          const response = await dashboardService.getTableCounts();
          setTableCounts(response);
        } catch (err: unknown) {
          setError(getUserFriendlyErrorMessage(err as ApiError));
          console.error('Error fetching table counts:', err);
        }
        finishStep();
      }
      if (shouldLoadRetention) {
        reportStep('Retention verisi yükleniyor…');
        try {
          const snap = normalizeRetentionCountries(countriesSnapshot);
          let retention: RetentionData[];
          if (snap[0] === 'Tüm Ülkeler') {
            retention = await dashboardService.getRetentionData({ country_code: null });
          } else {
            retention = await dashboardService.getRetentionData({
              country_code: getCountryCode(snap[0]),
            });
          }
          setRetentionData(retention);
        } catch (err) {
          console.error('Error fetching retention data:', err);
        }
        finishStep();
      }

      if (shouldLoadAppMetrics) {
        reportStep('Uygulama metrikleri yükleniyor…');
        try {
          const snap = normalizeRetentionCountries(countriesSnapshot);
          let appMetrics: AppMetricsResponse[];
          if (snap[0] === 'Tüm Ülkeler') {
            appMetrics = await dashboardService.getAppMetrics({ country_code: null });
          } else {
            appMetrics = await dashboardService.getAppMetrics({
              country_code: getCountryCode(snap[0]),
            });
          }
          setAppMetricsData(appMetrics);
        } catch (err) {
          console.error('Error fetching app metrics:', err);
        }
        finishStep();
      }

      if (shouldLoadCharts) {
        reportStep('Genel metrik özeti yükleniyor…');
        try {
          setOverviewMetrics(await dashboardService.getAppIdMetricsOverview());
        } catch (err) {
          console.error('Error fetching app ID metrics overview:', err);
        }
        finishStep();

        for (const metricsType of DETAIL_TYPES) {
          reportStep(`${detailStepLabel(metricsType)} yükleniyor…`);
          try {
            const detail = await dashboardService.getAppIdMetricsDetails({ metrics_type: metricsType });
            setDetailMetrics(metricsType, detail);
          } catch (err) {
            console.error(`Error fetching chart metrics (${metricsType}):`, err);
          }
          finishStep();
        }
        setOverviewReady(true);
      }

      setLoading(false);
      if (!blockingOverlay) {
        setIsRefreshing(false);
      }
    } finally {
      if (!shouldLoadCharts) {
        setOverviewReady(true);
      }
      if (blockingOverlay) {
        setDashboardRefreshProgress(null);
        setDashboardBlockingLoading(false);
        setIsRefreshing(false);
      }
    }
  }, [
    setDetailMetrics,
    shouldLoadTableCounts,
    shouldLoadRetention,
    shouldLoadAppMetrics,
    shouldLoadCharts,
  ]);

  const loadRetentionOnly = useCallback(async (countries: string[]) => {
    if (!shouldLoadRetention) return;
    const id = ++retentionRequestId.current;
    setRetentionScopedLoading(true);
    try {
      const snap = normalizeRetentionCountries(countries);
      let data: RetentionData[];
      if (snap[0] === 'Tüm Ülkeler') {
        data = await dashboardService.getRetentionData({ country_code: null });
      } else {
        data = await dashboardService.getRetentionData({ country_code: getCountryCode(snap[0]) });
      }
      if (id !== retentionRequestId.current) return;
      setRetentionData(data);
    } catch (err) {
      console.error('Error fetching retention data:', err);
    } finally {
      if (id === retentionRequestId.current) {
        setRetentionScopedLoading(false);
      }
    }
  }, [shouldLoadRetention]);

  const loadAppMetricsOnly = useCallback(async (countries: string[]) => {
    if (!shouldLoadAppMetrics) return;
    const id = ++appMetricsRequestId.current;
    setAppMetricsScopedLoading(true);
    try {
      const snap = normalizeRetentionCountries(countries);
      let data: AppMetricsResponse[];
      if (snap[0] === 'Tüm Ülkeler') {
        data = await dashboardService.getAppMetrics({ country_code: null });
      } else {
        data = await dashboardService.getAppMetrics({ country_code: getCountryCode(snap[0]) });
      }
      if (id !== appMetricsRequestId.current) return;
      setAppMetricsData(data);
    } catch (err) {
      console.error('Error fetching app metrics:', err);
    } finally {
      if (id === appMetricsRequestId.current) {
        setAppMetricsScopedLoading(false);
      }
    }
  }, [shouldLoadAppMetrics]);

  useEffect(() => {
    void loadFullDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- yalnızca ilk yükleme
  }, []);

  const skipFirstCountryScopedEffect = useRef(true);
  useEffect(() => {
    if (skipFirstCountryScopedEffect.current) {
      skipFirstCountryScopedEffect.current = false;
      return;
    }
    void Promise.all([loadRetentionOnly(retentionCountries), loadAppMetricsOnly(retentionCountries)]);
  }, [retentionCountries, loadRetentionOnly, loadAppMetricsOnly]);

  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    void loadFullDashboard({ blockingOverlay: true });
  }, [loadFullDashboard]);

  /** Retention + Uygulama Metrikleri panoları (her birinin seçili ülkesi); tam dashboard yenilemez. */
  const handleRefreshRetentionAndAppMetrics = useCallback(() => {
    const snap = retentionCountriesRef.current;
    void Promise.all([loadRetentionOnly(snap), loadAppMetricsOnly(snap)]);
  }, [loadRetentionOnly, loadAppMetricsOnly]);

  return {
    retentionCountries,
    setRetentionCountries,
    selectedCountry,
    isRefreshing,
    tableCounts,
    retentionData,
    appMetricsData,
    overviewMetrics,
    appSessionMetrics,
    userMetrics,
    gameSessionMetrics,
    testSessionMetrics,
    chatSessionMetrics,
    advertisementMetrics,
    loading,
    retentionScopedLoading,
    appMetricsScopedLoading,
    countryScopedLoading,
    dashboardBlockingLoading,
    dashboardRefreshProgress,
    overviewReady,
    error,
    handleRefresh,
    handleRefreshRetentionAndAppMetrics,
  };
}
